import { styled } from 'baseui'
import { FlexColumn, FLOATING_GAP } from '@/shared/theme/styled-components'
import {
  RAIL_BUTTON_INSET,
  RAIL_BUTTON_WIDTH,
  RAIL_EASE,
  RAIL_ICON_SIZE,
  MENU_MOTION,
  MENU_OPEN_WIDTH_VARIABLE,
  MENU_RAIL_WIDTH,
  railContentFadeIn,
  railIconHover,
  railSlideIn,
  railStagger,
  railStaggerDelay,
} from '@/shared/theme/railSlot'
import { belowBanner, viewportHeightBelowBanner } from '@/shared/theme/bannerHeight'

const floatingMenu = ($theme, $expanded) => ({
  position: 'sticky',
  top: belowBanner(FLOATING_GAP),
  height: viewportHeightBelowBanner(FLOATING_GAP),
  overflowX: 'hidden',
  overflowY: 'auto',
  width: $expanded ? `var(${MENU_OPEN_WIDTH_VARIABLE}, max-content)` : MENU_RAIL_WIDTH,
  minWidth: $expanded ? '12rem' : 0,
  padding: `${FLOATING_GAP} 0.5rem`,
  borderRadius: '0.75rem',
  alignItems: $expanded ? 'stretch' : 'center',
  backgroundColor: $expanded ? $theme.colors.bgPulseCanvas : $theme.colors.white,
  transition: `width ${MENU_MOTION}, min-width ${MENU_MOTION}, background-color ${MENU_MOTION}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
})

export const menuSlot = ($fill, $expanded) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  containerType: $expanded ? 'normal' : 'size',
  width: '100%',
  flex: '1 1 0',
  minHeight: '1.6875rem',
  maxHeight: $fill ? '3rem' : '2.25rem',
  ...railStagger(24, 30),
})

const selectedSurface = ($theme, $expanded) =>
  $expanded
    ? { backgroundColor: $theme.colors.white, boxShadow: $theme.lighting.shadowPulseMenuSelected }
    : { backgroundColor: $theme.colors.bgPulseSelected, boxShadow: 'none' }

const RAIL_TILE = `min(${RAIL_BUTTON_WIDTH}, 100cqh - 0.375rem)`

const RAIL_TILE_OFFSET = `calc((${RAIL_BUTTON_WIDTH} - ${RAIL_TILE}) / 2)`

const selectedMark = ($theme, $expanded) => ({
  content: '""',
  position: 'absolute',
  top: '50%',
  left: $expanded ? '-0.5rem' : `calc(-0.5rem - ${RAIL_TILE_OFFSET})`,
  width: '0.1875rem',
  height: '1.125rem',
  marginTop: '-0.5625rem',
  borderTopRightRadius: '0.1875rem',
  borderBottomRightRadius: '0.1875rem',
  backgroundColor: $theme.colors.primaryPulse,
  animationName: { from: { transform: 'scaleY(0)' }, to: { transform: 'none' } },
  animationDuration: '220ms',
  animationTimingFunction: RAIL_EASE,
  '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
})

export const floatingButton = ($theme, $expanded, $selected = false) => ({
  position: 'relative',
  width: $expanded ? '100%' : RAIL_TILE,
  height: $expanded ? `min(${RAIL_BUTTON_WIDTH}, 100% - 0.375rem)` : RAIL_TILE,
  marginLeft: $expanded ? 0 : RAIL_TILE_OFFSET,
  padding: $expanded ? `0 ${RAIL_BUTTON_INSET}` : `0 0 0 calc((${RAIL_TILE} - ${RAIL_ICON_SIZE}px) / 2)`,
  justifyContent: 'flex-start',
  gap: '0.75rem',
  borderTopLeftRadius: '0.5rem',
  borderTopRightRadius: '0.5rem',
  borderBottomRightRadius: '0.5rem',
  borderBottomLeftRadius: '0.5rem',
  color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseStrong,
  fontWeight: $selected ? 600 : 500,
  ...($selected ? selectedSurface($theme, $expanded) : { backgroundColor: 'transparent', boxShadow: 'none' }),
  transition: 'background-color 0.12s, box-shadow 0.12s',
  ':hover': $selected
    ? selectedSurface($theme, $expanded)
    : { backgroundColor: $expanded ? $theme.colors.white : $theme.colors.bgPulseCanvas },
  ...railIconHover,
  ...($selected ? { '::before': selectedMark($theme, $expanded) } : {}),
})

export const StyledSidebarMenu = styled(FlexColumn, ({ $theme, $floating, $expanded }) => ({
  alignItems: 'center',
  padding: '1rem',
  height: '100%',
  width: '100%',
  backgroundColor: $theme.colors.white,
  ...($floating ? floatingMenu($theme, $expanded) : {}),
}))

export const StyledMenuToggleRow = styled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  flexShrink: 0,
  width: '100%',
  marginBottom: '0.25rem',
})

export const StyledSidebarLabel = styled('span', {
  fontSize: '0.875rem',
  fontWeight: 'inherit',
  lineHeight: '1.25rem',
  whiteSpace: 'nowrap',
  color: 'inherit',
  animationName: railSlideIn,
  ...railContentFadeIn,
  animationDuration: '220ms',
  animationDelay: railStaggerDelay(40),
  animationFillMode: 'backwards',
  transition: 'opacity 140ms ease-in, transform 140ms ease-in, display 140ms allow-discrete',
  ':is([hidden])': { display: 'none', opacity: 0, transform: 'translateX(-0.375rem)' },
})

// Not FlexCenter: its second justify-content class could win over the open menu's left alignment.
const flexCentred = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const StyledSidebarButton = styled('div', ({ $theme, $isSelected, $floating, $expanded }) => ({
  ...flexCentred,
  padding: '.25rem',
  backgroundColor: $isSelected ? $theme.colors.primary : $theme.colors.white,
  outlineOffset: '-.125rem',

  borderTopLeftRadius: $theme.borders.radius200,
  borderTopRightRadius: $theme.borders.radius200,
  borderBottomRightRadius: $theme.borders.radius200,
  borderBottomLeftRadius: $theme.borders.radius200,

  ':hover': {
    outline: `.125rem solid ${$theme.colors.primary}`,
  },

  ':not(:last-child)': {
    marginBottom: '1rem',
  },

  ...($floating ? { ...floatingButton($theme, $expanded, $isSelected), ':not(:last-child)': { marginBottom: 0 } } : {}),
}))

export const StyledSidebarDivButton = styled('div', ({ $theme, $isSelected, $floating, $expanded }) => ({
  ...flexCentred,
  padding: '.25rem',
  backgroundColor: $isSelected ? $theme.colors.primary : $theme.colors.white,
  outlineOffset: '-.125rem',
  width: 'auto',

  borderTopLeftRadius: $theme.borders.radius200,
  borderTopRightRadius: $theme.borders.radius200,
  borderBottomRightRadius: $theme.borders.radius200,
  borderBottomLeftRadius: $theme.borders.radius200,

  ':hover': {
    outline: `.125rem solid ${$theme.colors.primary}`,
  },

  marginBottom: '1rem',

  ...($floating ? { ...floatingButton($theme, $expanded, $isSelected), marginBottom: 0 } : {}),
}))

export const logoOverrides = () => ({
  Svg: {
    style: () => ({
      height: 'auto',
      marginBottom: '0.5rem',
    }),
  },
})

export const tooltipOverrides = () => ({
  Body: {
    style: ({ $theme }) => ({
      margin: '.25rem',

      backgroundColor: 'none',

      boxShadow: $theme.lighting.shadow600,

      borderTopLeftRadius: $theme.borders.radius200,
      borderTopRightRadius: $theme.borders.radius200,
      borderBottomRightRadius: $theme.borders.radius200,
      borderBottomLeftRadius: $theme.borders.radius200,
    }),
  },
  Inner: {
    style: ({ $theme }) => ({
      backgroundColor: $theme.colors.white,

      color: $theme.colors.mono700,

      borderTopLeftRadius: $theme.borders.radius200,
      borderTopRightRadius: $theme.borders.radius200,
      borderBottomRightRadius: $theme.borders.radius200,
      borderBottomLeftRadius: $theme.borders.radius200,
      border: `2px solid ${$theme.colors.bg150}`,
    }),
  },
})
